import React, { useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export const AdminLayout: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Note: In production, check user.isAdmin on the backend
  // For now, we'll rely on backend authorization

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h1 style={styles.logo}>VibeTalk Admin</h1>
          <p style={styles.adminEmail}>{user?.email}</p>
        </div>

        <nav style={styles.nav}>
          <NavLink
            to="/admin"
            end
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            <span style={styles.navIcon}>📊</span>
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/admin/reports"
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            <span style={styles.navIcon}>🚨</span>
            <span>Reports</span>
          </NavLink>

          <NavLink
            to="/admin/users"
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            <span style={styles.navIcon}>👥</span>
            <span>Users</span>
          </NavLink>

          <NavLink
            to="/admin/audit-log"
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            <span style={styles.navIcon}>📋</span>
            <span>Audit Log</span>
          </NavLink>
        </nav>

        <div style={styles.sidebarFooter}>
          <NavLink to="/" style={styles.backLink}>
            ← Back to App
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
  },
  sidebar: {
    width: '260px',
    backgroundColor: '#1e293b',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    height: '100vh',
    left: 0,
    top: 0,
  },
  sidebarHeader: {
    padding: '2rem 1.5rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  logo: {
    fontSize: '1.5rem',
    fontWeight: '700',
    margin: '0 0 0.5rem',
    color: 'white',
  },
  adminEmail: {
    fontSize: '0.85rem',
    color: 'rgba(255, 255, 255, 0.6)',
    margin: 0,
  },
  nav: {
    flex: 1,
    padding: '1.5rem 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.875rem 1.5rem',
    color: 'rgba(255, 255, 255, 0.7)',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    borderLeft: '3px solid transparent',
  },
  navLinkActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    borderLeftColor: '#f59e0b',
  },
  navIcon: {
    fontSize: '1.25rem',
  },
  sidebarFooter: {
    padding: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  backLink: {
    display: 'block',
    color: 'rgba(255, 255, 255, 0.7)',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'color 0.2s ease',
  },
  main: {
    flex: 1,
    marginLeft: '260px',
    padding: '2rem',
    minHeight: '100vh',
  },
};
